import type { Vec3 } from "@dust/world/internal";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

const BLUEPRINT_API_URL = "https://blueprint-worker.permutationcity.org";

export type BlueprintItem = {
  objectTypeId: number;
  position: Vec3;
  orientation?: number;
};

export type Blueprint = BlueprintItem[];

async function fetchPlayerPositionBlueprint(playerPosition: Vec3) {
  try {
    const response = await fetch(
      `${BLUEPRINT_API_URL}/?x=${playerPosition[0]}&y=${playerPosition[1]}&z=${playerPosition[2]}`,
    );
    return response.json();
  } catch (error) {
    console.error(error);
    return [];
  }
}

async function fetchCursorBlueprint(cursorPosition: Vec3) {
  try {
    const response = await fetch(
      `${BLUEPRINT_API_URL}/?x=${cursorPosition[0]}&y=${cursorPosition[1]}&z=${cursorPosition[2]}`,
    );
    return response.json();
  } catch (error) {
    console.error(error);
    return [];
  }
}

export function useBlueprintQuery({
  playerPosition,
  cursorPosition,
}: {
  playerPosition: Vec3;
  cursorPosition: Vec3 | null;
}) {
  return useQuery<Blueprint>({
    queryKey: ["blueprint", playerPosition, cursorPosition],
    queryFn: async () => {
      const playerPositionBlueprint =
        await fetchPlayerPositionBlueprint(playerPosition);

      const finalBlueprint = [...(playerPositionBlueprint ?? [])];
      if (
        cursorPosition &&
        !(
          cursorPosition[0] === playerPosition[0] &&
          cursorPosition[1] === playerPosition[1] &&
          cursorPosition[2] === playerPosition[2]
        )
      ) {
        try {
          const cursorBlueprint = await fetchCursorBlueprint(cursorPosition);
          finalBlueprint.push(...cursorBlueprint);
        } catch (error) {
          console.error(error);
        }
      }

      const blueprint = finalBlueprint.map(
        ({ objectTypeId, position, orientation }) => ({
          objectTypeId,
          position,
          orientation,
        }),
      );

      return blueprint;
    },
    placeholderData: keepPreviousData,
    refetchIntervalInBackground: true,
    enabled: !!playerPosition,
    staleTime: 0,
  });
}
